import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

import classNames from "classnames";
import type React from "react";

import { ReactComponent as Icon_Close16 } from "../../assets/icons/CloseIcon16.svg";

import style from "./TagInput.module.css";

const separators: ReadonlyArray<string> = ["Enter", "Tab", ",", " "];
const separatorRegex: Readonly<RegExp> = /[\s\t,\n\r]/;

export interface TagInputRef {
  clearInput: () => void;
  focus: () => void;
  blur: () => void;
}

interface TagInputProps {
  deleteOnBackspace?: boolean;
  allowDuplicates?: boolean;
  errors?: string[];

  name?: string;
  values: string[];
  onChange?: (value: string[]) => void;
  onAdd?: (value: string) => void;
  onRemove?: (value: string) => void;
  onBlur?: (value: string[]) => void;
  onFocus?: () => void;

  defaultValue?: string[];
  className?: string;
  maxRows?: number;

  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void;
  tokenizeOnBlur?: boolean;
}

export const TagInput = forwardRef<TagInputRef, TagInputProps>(
  (
    {
      deleteOnBackspace = true,
      allowDuplicates = false,
      errors,
      onChange,
      onAdd,
      onRemove,
      onBlur,
      onFocus,
      className,
      values,
      maxRows,
      tokenizeOnBlur,
      ...attrs
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tagRefs = useRef<(HTMLDivElement | null)[]>([]);

    const clearInput = useCallback(() => {
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.style.setProperty("--dynamic-width", "0ch");
      }
    }, []);

    const focusInput = useCallback(() => {
      inputRef.current?.focus();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        clearInput,
        focus: focusInput,
        blur: () => {
          inputRef.current?.blur();
        },
      }),
      [clearInput, focusInput]
    );

    const scrollToBottom = () => {
      if (wrapperRef.current && maxRows) {
        requestAnimationFrame(() => {
          if (wrapperRef.current) {
            wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;
          }
        });
      }
    };

    const ensureInputVisible = useCallback(() => {
      if (wrapperRef.current && inputRef.current && maxRows) {
        requestAnimationFrame(() => {
          if (!inputRef.current) {
            return;
          }
          inputRef.current?.scrollIntoView({ block: "nearest" });
        });
      }
    }, [maxRows]);

    const focusTag = useCallback(
      (index: number) => {
        if (index >= 0 && index < values.length && tagRefs.current[index]) {
          tagRefs.current[index]?.focus();
        }
      },
      [values.length]
    );

    const removeTag = useCallback(
      (index: number) => {
        const newValues = values.filter((_, i) => i !== index);
        onChange?.(newValues);
        onRemove?.(values[index]);

        const removedTagWasFocused = document.activeElement === tagRefs.current[index];

        if (removedTagWasFocused) {
          requestAnimationFrame(() => {
            if (newValues.length === 0) {
              focusInput();
            } else if (index === newValues.length) {
              focusTag(index - 1);
            } else {
              focusTag(index);
            }
          });
        }
      },
      [values, onChange, onRemove, focusInput, focusTag]
    );

    return (
      <div
        ref={wrapperRef}
        className={classNames(
          style.wrapper,
          className,
          attrs.disabled && style.disabled,
          maxRows && style.limitedRows
        )}
        style={{ "--max-rows": maxRows } as React.CSSProperties}
      >
        {values?.map((value, index) => (
          <div
            key={`${index}-${value}`}
            ref={(el) => { tagRefs.current[index] = el; }}
            className={classNames(style.tag, errors?.includes(value) && style.error)}
            tabIndex={-1}
            onKeyDown={(e) => {
              if (attrs.disabled) return;

              switch (e.key) {
                case "Delete":
                case "Backspace":
                  e.preventDefault();
                  removeTag(index);
                  break;
                case "ArrowLeft":
                  e.preventDefault();
                  if (index > 0) {
                    focusTag(index - 1);
                  }
                  break;
                case "ArrowRight":
                  e.preventDefault();
                  if (index < values.length - 1) {
                    focusTag(index + 1);
                  } else {
                    focusInput();
                  }
                  break;
              }
            }}
          >
            <span>{value}</span>
            <button
              className={style.tagButton}
              disabled={attrs.disabled}
              type="button"
              onClick={() => removeTag(index)}
              tabIndex={-1}
            >
              <Icon_Close16 />
            </button>
          </div>
        ))}
        <input
          type="text"
          {...attrs}
          placeholder={attrs.placeholder || undefined}
          className={classNames(style.textField)}
          ref={inputRef}
          onFocus={() => {
            onFocus?.();
          }}
          onInput={(e) => {
            ensureInputVisible();
            e.currentTarget.style.setProperty(
              "--dynamic-width",
              `${e.currentTarget.value.length}ch`
            );
            attrs.onInput?.(e);
          }}
          onBlur={(e) => {
            const value = e.currentTarget.value.trim();
            if (tokenizeOnBlur) {
              if (value) {
                onChange?.([...values, value]);
                onAdd?.(value);
                clearInput();
              }
            }
            onBlur?.([...values, value]);
          }}
          onPaste={(e) => {
            e.preventDefault();

            const value = e.clipboardData.getData("text");
            if (!value) {
              return;
            }

            const splitValues = value.split(separatorRegex).filter((v) => v.trim() !== "");

            let dedupedValues = splitValues;
            if (!allowDuplicates) {
              dedupedValues = [...new Set(splitValues)].filter((v) => !values.includes(v));
            }

            onChange?.([...values, ...dedupedValues]);
            dedupedValues.forEach((v) => onAdd?.(v));
            if (dedupedValues.length > 0) {
              scrollToBottom();
            }
          }}
          onKeyDown={(e) => {
            attrs.onKeyDown?.(e);

            const isSeparatorAndNotEmpty =
              separators?.includes(e.key) && e.currentTarget.value.trim() !== "";
            if (isSeparatorAndNotEmpty) {
              e.preventDefault();
            }

            const value = e.currentTarget.value;

            if (!value && e.key === "ArrowLeft" && values.length > 0) {
              e.preventDefault();
              focusTag(values.length - 1);
              return;
            }

            if (!value) {
              if (
                e.key === "Backspace" &&
                deleteOnBackspace &&
                (values?.length ?? 0) > 0 &&
                (e.metaKey || e.ctrlKey)
              ) {
                onChange?.([]);
                values?.forEach((v) => onRemove?.(v));
              } else if (e.key === "Backspace" && deleteOnBackspace && (values?.length ?? 0) > 0) {
                e.preventDefault();
                focusTag(values.length - 1);
              }

              return;
            }
            if (isSeparatorAndNotEmpty) {
              const trimmedValue = value.trim();
              if (!allowDuplicates && values?.includes(trimmedValue)) {
                clearInput();
                return;
              }

              onChange?.([...values, trimmedValue]);
              onAdd?.(trimmedValue);
              clearInput();
              scrollToBottom();
            }
          }}
        />
      </div>
    );
  }
);

TagInput.displayName = "TagInput";
